import React from 'react';
import { Alert, Col, Row } from 'antd';
import type { TodayFreshness, TodayMetric, TodayResponse } from '@/types';
import { TodayMiniChart } from './TodayMiniChart';
import { TodayTile, type TodayPace } from './TodayTile';
import { formatCount, formatInr, istClock, paceHiddenReason, pacePercent } from './todayMath';

export const ALL_DEAD_MESSAGE = 'Live counters are not updating — numbers may be stale.';

const ADS_STALE_HINT = 'BigQuery snapshot older than 2 hours';
const ADS_DEAD_HINT = 'No BigQuery snapshot today (job off, not connected, or no data yet)';

const LARGE_COL = { xs: 24, sm: 12, xl: 6 } as const;
const SMALL_COL = { xs: 12, md: 8, xl: 4 } as const;

const fixed1 = (n: number) => `${n.toFixed(1)}`;

interface Props {
  data: TodayResponse;
}

/** From when one group of fields is complete, today and yesterday (HH:MM IST, null = whole day). */
interface Coverage {
  today: string | null;
  yesterday: string | null;
}

/** The tiles of the Today page for one response. Pure: no fetching, no clock. */
const TodayBoardBase: React.FC<Props> = ({ data }) => {
  const { totals, yesterdaySameTime, freshness, money, ads, activeNow, hourly } = data;
  const byTime = istClock(data.asOf);
  // Two groups of tiles, each with its own coverage. The 30-minute replica job
  // rewrites players, purchases/payers and IAP revenue (Players today,
  // Purchases, Money's IAP part), so those are complete through the hour it
  // last wrote. New users, matches, logins, runs and rewarded ads come from the
  // live hour counters only and count from `countingSince`.
  const counter: Coverage = { today: data.countingSince, yesterday: data.yesterdayCountingSince };
  const reconciled: Coverage = {
    today: data.countingSinceReconciled,
    yesterday: data.yesterdayCountingSinceReconciled,
  };

  const allDead = Object.values(freshness).every((f) => f === 'dead');

  // Pace compares with yesterday at the same time, so it is hidden while
  // either day is partial for the tile's fields: it would show a false ▲/▼.
  const paceOf = (metric: TodayMetric, coverage: Coverage, scope?: string): TodayPace => ({
    percent: pacePercent(totals[metric], yesterdaySameTime[metric]),
    byTime,
    scope,
    hiddenReason: paceHiddenReason(coverage.today, coverage.yesterday),
  });

  const activeFreshness: TodayFreshness = activeNow.value === null ? 'dead' : 'ok';
  const finishPct =
    totals.matches_started > 0
      ? `${fixed1((totals.matches_completed / totals.matches_started) * 100)}% finish`
      : '— finish';
  const avgRuns =
    totals.matches_completed > 0
      ? formatCount(Math.round(totals.runs / totals.matches_completed))
      : '—';

  const storeCutPct = Math.round((1 - money.netShare) * 100);
  const adsClock = ads ? istClock(ads.asOf) : '';
  const moneyInfo = (
    <>
      Net IAP = store price × {money.netShare} ({storeCutPct}% store cut); gross{' '}
      {formatInr(money.iapGrossInr)}.{' '}
      {ads
        ? `Ads = network-reported earnings, BigQuery ${adsClock} + estimate, at ₹${money.usdInr}/$`
        : `Ads = network-reported earnings, not connected yet, at ₹${money.usdInr}/$`}
    </>
  );

  return (
    <>
      {allDead && (
        <Alert type="warning" showIcon message={ALL_DEAD_MESSAGE} style={{ marginBottom: 12 }} />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col {...LARGE_COL}>
          <TodayTile
            size="large"
            label="Active now"
            value={formatCount(activeNow.value)}
            freshness={activeFreshness}
            detail={
              activeNow.peak !== null && activeNow.peakAt
                ? `peak today ${formatCount(activeNow.peak)} at ${activeNow.peakAt}`
                : 'peak today —'
            }
          />
        </Col>
        <Col {...LARGE_COL}>
          <TodayTile
            size="large"
            label="New users"
            value={formatCount(totals.new_users)}
            freshness={freshness.new_users}
            pace={paceOf('new_users', counter)}
            countingSince={counter.today}
            chart={
              <TodayMiniChart
                today={hourly.today}
                yesterday={hourly.yesterday}
                metric="new_users"
                format={formatCount}
              />
            }
          />
        </Col>
        <Col {...LARGE_COL}>
          <TodayTile
            size="large"
            label="Matches completed"
            value={formatCount(totals.matches_completed)}
            freshness={freshness.matches_completed}
            pace={paceOf('matches_completed', counter)}
            detail={`started ${formatCount(totals.matches_started)} · ${finishPct}`}
            countingSince={counter.today}
            chart={
              <TodayMiniChart
                today={hourly.today}
                yesterday={hourly.yesterday}
                metric="matches_completed"
                format={formatCount}
              />
            }
          />
        </Col>
        <Col {...LARGE_COL}>
          <TodayTile
            size="large"
            label="Money today (₹ net)"
            value={formatInr(money.totalInr)}
            freshness={freshness.revenue_inr}
            pace={paceOf('revenue_inr', reconciled, 'IAP')}
            detail={`IAP ${formatInr(money.iapNetInr)} · Ads ${
              money.adsInr === null ? 'not connected' : `≈ ${formatInr(money.adsInr)}`
            }`}
            countingSince={reconciled.today}
            info={moneyInfo}
            chart={
              <TodayMiniChart
                today={hourly.today}
                yesterday={hourly.yesterday}
                metric="revenue_inr"
                scale={money.netShare}
                format={formatInr}
                label="IAP net"
              />
            }
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col {...SMALL_COL}>
          <TodayTile
            size="small"
            label="Players today"
            value={formatCount(totals.players)}
            freshness={freshness.players}
            pace={paceOf('players', reconciled)}
            countingSince={reconciled.today}
          />
        </Col>
        <Col {...SMALL_COL}>
          <TodayTile
            size="small"
            label="Logins"
            value={formatCount(totals.logins)}
            freshness={freshness.logins}
            pace={paceOf('logins', counter)}
            countingSince={counter.today}
          />
        </Col>
        <Col {...SMALL_COL}>
          <TodayTile
            size="small"
            label="Runs scored"
            value={formatCount(totals.runs)}
            freshness={freshness.runs}
            pace={paceOf('runs', counter)}
            detail={`avg ${avgRuns} per match`}
            countingSince={counter.today}
          />
        </Col>
        <Col {...SMALL_COL}>
          <TodayTile
            size="small"
            label="Purchases"
            value={formatCount(totals.purchases)}
            freshness={freshness.purchases}
            pace={paceOf('purchases', reconciled)}
            detail={`${formatCount(totals.payers)} payers`}
            countingSince={reconciled.today}
          />
        </Col>
        <Col {...SMALL_COL}>
          <TodayTile
            size="small"
            label="Rewarded ads"
            value={formatCount(totals.ads_watched)}
            freshness={freshness.ads_watched}
            pace={paceOf('ads_watched', counter)}
            detail={
              ads && ads.inrPerAd !== null ? `₹${ads.inrPerAd.toFixed(2)} per ad` : '₹ per ad —'
            }
            countingSince={counter.today}
          />
        </Col>
        <Col {...SMALL_COL}>
          <TodayTile
            size="small"
            label="Ad revenue"
            value={ads ? `$${ads.estimatedUsd.toFixed(2)}` : 'not connected'}
            freshness={freshness.ads}
            // With a snapshot on screen, 'dead' means the live counters stopped
            // (the estimate is not moving), so the default hint stays.
            freshnessHints={
              ads ? { stale: ADS_STALE_HINT } : { stale: ADS_STALE_HINT, dead: ADS_DEAD_HINT }
            }
            // "+ est." only when the backend added an estimate (not on a partial day).
            detail={
              ads ? `BigQuery ${adsClock}${ads.estimatedUsd > ads.usd ? ' + est.' : ''}` : undefined
            }
          />
        </Col>
      </Row>
    </>
  );
};

/**
 * Memoised: the page re-renders when its auto-refresh schedule moves, which
 * must not redraw the tiles and charts unless the data changed.
 */
export const TodayBoard = React.memo(TodayBoardBase);
